import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Route, Switch, withRouter } from "react-router-dom";
import EntitiesDashboard from "./components/EntitiesDashboard";
import NotFound from "./components/NotFound";
import MainOutlet from "./navigation/MainOutlet";
import OtherOutlet from "./navigation/OtherOutlet";
import Entities from "./components/Entities";
import Login from "./authentication/Login";
import Verification from "./authentication/Verification";
import GuestVerification from "./authentication/GuestVerification";
import AuthOutlet from "./navigation/AuthOutlet";
import PortalLoad from "./navigation/PortalLoad";
import ConsentRequired from './components/ConsentRequired';
import ResetUnlockCode from './components/ResetUnlockCode';
import ErrorHandling from './components/ErrorHandling';
import AuthGuard from "./authentication/AuthGuard";
import PrivateGuard from "./authentication/PrivateGuard";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { actionCreators as staticDataActions } from "./redux/actions/staticDataActions";
import { actionCreators as authActions } from "./redux/actions/authActions";
import { getAccountUniqueNameFromUrl, buildRouteWithAuthParams } from './authentication/authHelpers';
import Home from "./components/Home";
import Legal from './authentication/Legal';
import IDVerification from "./components/IDVerification";

class App extends React.Component {
    async componentDidMount() {
        if (!isOtherRoute()) {
            const accountUniqueName = getAccountUniqueNameFromUrl();

            await this.props.requestPortalSettings(accountUniqueName);

            const params = new URLSearchParams(this.props.location.search);
            const accessCode = params.get('c');

            const valid = await this.props.checkAccountMismatch(accessCode);

            if (!valid) {
                this.props.history.push(buildRouteWithAuthParams('/login/guest-verification', new URLSearchParams(this.props.location.search)));
            }
        }
    }

    render() {
        const theme = createTheme({
            palette: this.props.portalSettings.palette
        });

        return (
            <>
                {isOtherRoute() ?
                    <Switch>
                        <Route exact path="/home" component={OtherRoutes} />
                        <Route path="/error" component={OtherRoutes} />
                        <Route exact path="/" component={OtherRoutes} />

                    </Switch>
                    :
                    !this.props.isSetupComplete ? (
                        <PortalLoad />
                    ) : (
                        <ThemeProvider theme={theme}>
                            <Switch>
                                <Route path="/:accountUniqueName/login" component={AuthRoutes} />
                                <Route path="/:accountUniqueName/acceptance-of-terms/:id" component={AuthRoutes} />
                                <Route path="/:accountUniqueName/acceptance-of-terms" component={AuthRoutes} />
                                <Route path="/:accountUniqueName/entities/:entityChangeRequestId/id-verification" component={MainRoutes} />
                                <Route path="/error" component={AuthRoutes} />
                                <Route path="/:accountUniqueName" component={MainRoutes} />
                            </Switch>
                        </ThemeProvider>
                    )
                }
            </>
        );
    }
}

function isOtherRoute() {

    let otherRouteUrlSegments = ["", "home", "error"];

    let urlSegments = window.location.href.split("/");

    let segmentToCheck = (urlSegments[3] && urlSegments[3].includes('?')) ? urlSegments[3].substring(0, urlSegments[3].indexOf('?')) : urlSegments[3];

    return otherRouteUrlSegments.includes(segmentToCheck);
}

function RouteWrapper({ component: Component, ...rest }) {
    const { setUseDefaultLogo, ...otherProps } = rest;

    return (
        <Route {...otherProps} render={(props) => (<Component {...props} setUseDefaultLogo={setUseDefaultLogo} />)} />
    );
};

//-------child roots
function SwitchWrapper(props) {
    const mappedChildren = React.Children.map(props.children, child => {
        if (React.isValidElement(child)) {
            return React.cloneElement(child, { setUseDefaultLogo: props.setUseDefaultLogo });
        }
        return child;
    });
    return (
        <Switch>{mappedChildren}</Switch>
    );
}

function AuthRoutes(props) {
    return (
        <AuthView>
            <SwitchWrapper>
                <Route
                    path="/error"
                    component={ErrorHandling}
                />
                <Route exact path="/:accountUniqueName/login" component={Login} />
                <Route
                    path="/:accountUniqueName/login/verification"
                    component={AuthGuard(Verification)}
                />
                <Route
                    path="/:accountUniqueName/login/guest-verification"
                    component={GuestVerification}
                />
                <RouteWrapper
                    path="/:accountUniqueName/acceptance-of-terms/:id"
                    component={AuthGuard(Legal)}
                />
                <Route
                    exact
                    path="/:accountUniqueName/acceptance-of-terms"
                    component={AuthGuard(ConsentRequired)}
                />
                <Route
                    exact
                    path="/:accountUniqueName/login/reset-unlock-code"
                    component={ResetUnlockCode}
                />
                <Route component={NotFound} />
            </SwitchWrapper>
        </AuthView>
    );
}

function MainRoutes() {
    return (
        <MainView>
            <Switch>
                <Route
                    exact
                    path="/:accountUniqueName"
                    component={PrivateGuard(EntitiesDashboard)}
                />
                <Route
                    exact
                    path="/:accountUniqueName/entities"
                    component={PrivateGuard(EntitiesDashboard)}
                />
                <Route
                    exact
                    path="/:accountUniqueName/entities/:entityChangeRequestId/id-verification"
                    component={PrivateGuard(IDVerification)}
                />
                <Route
                    path="/:accountUniqueName/entities/:entityId"
                    component={PrivateGuard(Entities)}
                />
                {/* <Route
          exact
          path="/:accountUniqueName/revoke-account"
          component={PrivateGuard(RevokeAccount)}
        /> */}
                <Route
                    exact
                    path="/:accountUniqueName/error"
                    component={PrivateGuard(ErrorHandling)}
                />
                <Route component={PrivateGuard(NotFound)} />
            </Switch>
        </MainView>
    );
}

function OtherRoutes() {
    return (
        <OtherView>
            <Switch>
                <Route exact path="/home" component={Home} />
                <Route exact path="/error" component={ErrorHandling} />
                <Route exact path="/" component={Home} />
            </Switch>
        </OtherView>
    );
}

//-------layouts

function AuthView(props) {
    return <AuthOutlet rootOutlet={props.children} {...props} />;
}

function MainView(props) {
    return <MainOutlet rootOutlet={props.children} />;
}

function OtherView(props) {
    return <OtherOutlet rootOutlet={props.children} />;
}

export default connect(
    (state) => Object.assign({}, state.staticData, state.authentication),
    (dispatch) => bindActionCreators(Object.assign({}, staticDataActions, authActions), dispatch)
)(withRouter(App));
